/* eslint-disable react/no-unescaped-entities, no-empty */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Lock, CheckCircle2, Loader2, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import { getEffectiveRef } from "@/lib/refTracker";
import { markToolCompleted } from "@/lib/journeyTracker";
import { readJourneyRef, saveJourneyRef } from "@/lib/journeyRef";
import { toast } from "sonner";

const TIPOLOGIE = ["Appartamento", "Casa indipendente", "Villa", "Villetta a schiera", "Rustico"];
const STATI = ["Ottimo", "Buono", "Da ristrutturare"];
const PIANI = ["Seminterrato", "Piano terra", "Primo piano", "Piano intermedio", "Ultimo piano o attico", "Più livelli"];
const PERTINENZE = ["Giardino", "Terrazzo", "Balcone", "Garage", "Posto auto", "Cantina", "Piscina", "Tettoia o soffitta", "Nessuna"];
const TEMPISTICHE = [
  { value: "Meno di 3 mesi (Vendita prioritaria)", label: "Meno di 3 mesi", help: "Vendita prioritaria" },
  { value: "Entro 6 mesi (Vendita pianificata)", label: "Entro 6 mesi", help: "Vendita pianificata" },
  { value: "Non ho ancora una scadenza (Esplorazione)", label: "Non ho ancora una scadenza", help: "Esplorazione" },
];
// CASO A del brief: il termine "Vendita" compare anche nel value tecnico spedito al backend.
// Per non alterare il payload lasciamo il value INVARIATO e branchiamo esclusivamente
// label/help mostrati all'utente. Payload.tempistica resta identico in entrambe le modalità.
const TEMPISTICHE_AFFITTO_HELP = {
  "Meno di 3 mesi (Vendita prioritaria)": "Locazione prioritaria",
  "Entro 6 mesi (Vendita pianificata)": "Locazione pianificata",
  "Non ho ancora una scadenza (Esplorazione)": "Esplorazione",
};
const getTempisticheView = (modalita) => {
  if (modalita !== "affitto") return TEMPISTICHE;
  return TEMPISTICHE.map((t) => ({ ...t, help: TEMPISTICHE_AFFITTO_HELP[t.value] || t.help }));
};
const STORAGE_KEY = "vg2_funnel_state";
const IDEMPOTENCY_STORAGE_KEY = "vg2_funnel_idempotency";
const MODALITA_STORAGE_KEY = "vg2_valutapro_modalita_choice";
const TOTAL_STEPS = 6;

function getOrCreateIdempotencyKey() {
  try {
    const existing = sessionStorage.getItem(IDEMPOTENCY_STORAGE_KEY);
    if (existing) return existing;
    const created = `valuta-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(IDEMPOTENCY_STORAGE_KEY, created);
    return created;
  } catch {
    return `valuta-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

export default function FunnelPage() {
  const [params] = useSearchParams();
  const refFromUrl = params.get("ref") || "";
  const emailFromUrl = params.get("email") || "";
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [whatsappUrl, setWhatsappUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submittedScore, setSubmittedScore] = useState(0);
  const [submittedNoAnswers, setSubmittedNoAnswers] = useState([]);
  const [submittedModalita, setSubmittedModalita] = useState("vendita");
  const [journeyMode, setJourneyMode] = useState(false);
  const [modalita, setModalita] = useState("vendita"); // "vendita" | "affitto"
  const [modalitaSource, setModalitaSource] = useState("default"); // default | journey | user
  const [idempotencyKey] = useState(getOrCreateIdempotencyKey);

  const [data, setData] = useState(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch {}
    return {
      tipologia: "", indirizzo: "", mq: "", stato_immobile: "", piano: "",
      pertinenze: [],
      doc: null, ape: null, comp: null,
      valutazione: null,
      tempistica: "",
      nome: "", telefono: "", email: emailFromUrl || "",
      ref: refFromUrl || getEffectiveRef() || "",
    };
  });

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [data]);

  useEffect(() => {
    const effective = refFromUrl || getEffectiveRef() || "";
    if (effective) setData((d) => ({ ...d, ref: effective }));
  }, [refFromUrl]);

  // Journey continuity: if a tokenized reference is present in localStorage
  // (persisted by /orizzonti/call after successful verify) hydrate identity
  // via the existing /api/orizzonti/verify endpoint. No re-entry needed.
  // If localStorage is empty but the URL carries j+t (email CTA from another
  // browser/device), reuse the same verify mechanism and, on success, restore
  // vg_journey_ref in the exact same {j,t} format used across the app.
  useEffect(() => {
    let cancelled = false;
    async function hydrate() {
      // 1) Recupera il journey_id corrente SOLO se non-scaduto (TTL client-side).
      //    Un vg_journey_ref legacy o troppo vecchio viene proattivamente
      //    scartato da readJourneyRef → una nuova analisi ValutaPro non
      //    eredita mai l'identità di un journey completato in passato.
      let ref = readJourneyRef();

      // 1bis) Se lo storage è vuoto/expired ma l'URL contiene j+t (email CTA
      //       da altro browser/dispositivo), usa quelli come sorgente. Non
      //       persistere nulla prima della verifica backend.
      let fromUrl = false;
      if (!ref) {
        const jUrl = params.get("j") || "";
        const tUrl = params.get("t") || "";
        if (jUrl && tUrl) {
          ref = { j: jUrl, t: tUrl };
          fromUrl = true;
        }
      }
      const currentJourneyId = ref?.j || null;

      // 2) Leggi la scelta esplicita utente dallo storage, ma applicala solo se
      //    appartiene allo stesso journey_id corrente (o se entrambe non hanno journey).
      let savedChoice = null;
      try {
        const raw = sessionStorage.getItem(MODALITA_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed && (parsed.modalita === "vendita" || parsed.modalita === "affitto")) {
          const savedJourney = parsed.journey_id || null;
          if (savedJourney === currentJourneyId) {
            savedChoice = parsed.modalita;
          } else {
            // journey diverso o obsoleto → butta la preferenza per non contaminare
            try { sessionStorage.removeItem(MODALITA_STORAGE_KEY); } catch { /* noop */ }
          }
        }
      } catch { /* storage KO, ignora */ }
      if (savedChoice) {
        setModalita(savedChoice);
        setModalitaSource("user");
      }

      // 3) Se non c'è journey ref valido, non contattare il backend.
      if (!ref || !ref.j || !ref.t) return;
      try {
        const r = await api.get("/orizzonti/verify", { params: { j: ref.j, t: ref.t } });
        if (cancelled || !r?.data?.ok) return;
        const nome = r.data.nome || "";
        const email = r.data.email || "";
        const refCode = r.data.referral_code || "";
        const intenzione = (r.data.intenzione || "").toLowerCase();
        if (!nome && !email) return;
        // Verify riuscito: solo ora è sicuro persistere vg_journey_ref quando
        // veniamo dall'URL email. Formato IDENTICO a quello scritto da
        // OrizzontiCall.jsx e Orizzonti.jsx.
        if (fromUrl) {
          saveJourneyRef({ j: ref.j, t: ref.t });
          try { markToolCompleted("orizzonti", { journey_id: ref.j }); } catch { /* noop */ }
        }
        setJourneyMode(true);
        setData((d) => ({
          ...d,
          nome: d.nome || nome,
          email: d.email || email,
          ref: d.ref || refCode || "",
        }));
        // Preselect ValutaPro modalità da intenzione, MA solo se l'utente non ha
        // già una scelta esplicita salvata per questo journey (precedenza 1 > 2).
        if (!savedChoice) {
          if (intenzione === "vendere") {
            setModalita("vendita");
            setModalitaSource("journey");
          } else if (intenzione === "affittare") {
            setModalita("affitto");
            setModalitaSource("journey");
          }
        }
      } catch {
        // Token scaduto o invalido: rimane il flusso autonomo (standalone).
        // Non salviamo nulla in localStorage se provenivamo dall'URL.
      }
    }
    hydrate();
    return () => { cancelled = true; };
  }, []);

  const upd = (patch) => setData((d) => ({ ...d, ...patch }));

  // Identity awareness: se nome + email sono già disponibili (da journey/URL),
  // saltiamo lo Step 6 e il progressivo diventa 5/5.
  const nomeOk = data.nome.trim().length >= 2;
  const emailOk = /^\S+@\S+\.\S+$/.test(data.email);
  const hasIdentity = nomeOk && emailOk;
  const missingOnlyNome = !nomeOk && emailOk;
  const missingOnlyEmail = nomeOk && !emailOk;
  const TOTAL_STEPS_EFFECTIVE = hasIdentity ? 5 : 6;

  const canStep1 = data.tipologia && data.indirizzo.trim() && Number(data.mq) > 0 && data.stato_immobile && data.piano;
  const canStep2 = data.doc !== null && data.ape !== null && data.comp !== null;
  const canStep3 = data.valutazione !== null;
  const canStep4 = !!data.tempistica;
  const canStep6 = nomeOk && emailOk;

  const noAnswers = useMemo(() => {
    const arr = [];
    if (data.doc === false) arr.push({ key: "doc", label: "Documentazione urbanistica e catastale" });
    if (data.ape === false) arr.push({ key: "ape", label: "Classe energetica aggiornata" });
    if (data.comp === false) {
      arr.push({
        key: "comp",
        label: modalita === "affitto"
          ? "Analisi competitiva locazione micro-zona"
          : "Analisi competitiva micro-zona",
      });
    }
    if (data.valutazione === false) {
      arr.push({
        key: "val",
        label: modalita === "affitto"
          ? "Stima aggiornata del canone"
          : "Valutazione professionale recente",
      });
    }
    return arr;
  }, [data, modalita]);

  const score = useMemo(() => {
    let s = 100;
    const weights = { doc: 18, ape: 15, comp: 12, val: 10 };
    noAnswers.forEach((n) => (s -= weights[n.key] || 8));
    return Math.max(40, s);
  }, [noAnswers]);

  const goNext = () => setStep((s) => Math.min(TOTAL_STEPS_EFFECTIVE, s + 1));
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const togglePertinenza = (p) => {
    setData((d) => {
      let arr = [...d.pertinenze];
      if (p === "Nessuna") {
        arr = arr.includes("Nessuna") ? [] : ["Nessuna"];
      } else {
        arr = arr.filter((x) => x !== "Nessuna");
        arr = arr.includes(p) ? arr.filter((x) => x !== p) : [...arr, p];
      }
      return { ...d, pertinenze: arr };
    });
  };

  const submit = async () => {
    if (!canStep6) return;
    setSubmitting(true);
    try {
      const payload = {
        tipologia: data.tipologia,
        indirizzo: data.indirizzo.trim(),
        mq: Number(data.mq),
        stato_immobile: data.stato_immobile,
        piano: data.piano,
        pertinenze: data.pertinenze.filter((p) => p !== "Nessuna"),
        risposta_doc: !!data.doc,
        risposta_ape: !!data.ape,
        risposta_competizione: !!data.comp,
        risposta_valutazione: !!data.valutazione,
        tempistica: data.tempistica,
        nome: data.nome.trim(),
        telefono: (data.telefono || "").trim(),
        email: data.email.trim(),
        referral_code_origine: data.ref || null,
        idempotency_key: idempotencyKey,
        modalita,
      };
      const r = await api.post("/leads", payload);
      setLeadId(r.data.id);
      setWhatsappUrl(r.data.whatsapp_url || null);
      setSubmittedScore(score);
      setSubmittedNoAnswers(noAnswers);
      setSubmittedModalita((r.data.modalita || modalita || "vendita").toLowerCase());
      setSubmitted(true);
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(IDEMPOTENCY_STORAGE_KEY);
      sessionStorage.removeItem(MODALITA_STORAGE_KEY);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'invio");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <SuccessScreen
        nome={data.nome}
        email={data.email}
        referral={data.ref}
        score={submittedScore}
        noAnswers={submittedNoAnswers}
        whatsappUrl={whatsappUrl}
        modalita={submittedModalita}
      />
    );
  }

  return (
    <div className="vg-funnel-bg">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-12">
        <div className="flex items-center justify-between mb-6">
          {step === 1 ? (
            <a
              href="/app"
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
              data-testid="funnel-back-btn"
            >
              <ArrowLeft size={16} /> Esci
            </a>
          ) : (
            <button
              onClick={goBack}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
              data-testid="funnel-back-btn"
            >
              <ArrowLeft size={16} /> Indietro
            </button>
          )}
          <Logo size={22} />
          <div className="text-xs text-gray-500" data-testid="funnel-step-indicator">
            Step {step} di {TOTAL_STEPS_EFFECTIVE}
          </div>
        </div>

        <div className="vg-progress mb-10">
          <div className="vg-progress-fill" style={{ width: `${(step / TOTAL_STEPS_EFFECTIVE) * 100}%` }} />
        </div>

        <div className="fade-slide" key={step}>
          {step === 1 && (
            <Step1
              data={data}
              upd={upd}
              togglePertinenza={togglePertinenza}
              canNext={canStep1}
              onNext={goNext}
              modalita={modalita}
              onChangeModalita={(m) => {
                // Nessun cambio: preserva integralmente lo state.
                if (m === modalita) return;
                setModalita(m);
                setModalitaSource("user");
                // Reset SELETTIVO: azzera solo le risposte semanticamente diverse
                // tra Vendita e Affitto (competizione + valutazione).
                // doc/ape/tempistica/dati immobile/nome/email/... vengono conservati.
                setData((d) => ({ ...d, comp: null, valutazione: null }));
                // Persisti la scelta ESPLICITA legandola al journey corrente per
                // evitare di contaminare journey diversi.
                try {
                  const journeyRef = readJourneyRef();
                  const jid = journeyRef?.j || null;
                  sessionStorage.setItem(
                    MODALITA_STORAGE_KEY,
                    JSON.stringify({ modalita: m, journey_id: jid }),
                  );
                } catch { /* storage KO, non bloccare l'UX */ }
              }}
              modalitaSource={modalitaSource}
            />
          )}
          {step === 2 && <Step2 data={data} upd={upd} canNext={canStep2} onNext={goNext} modalita={modalita} />}
          {step === 3 && <Step3 data={data} upd={upd} canNext={canStep3} onNext={goNext} modalita={modalita} />}
          {step === 4 && <Step4 data={data} upd={upd} canNext={canStep4} onNext={goNext} modalita={modalita} />}
          {step === 5 && (
            <Step5
              score={score}
              noAnswers={noAnswers}
              onNext={goNext}
              modalita={modalita}
              hasIdentity={hasIdentity}
              submit={submit}
              submitting={submitting}
            />
          )}
          {step === 6 && (
            <Step6
              data={data}
              upd={upd}
              canSubmit={canStep6}
              submit={submit}
              submitting={submitting}
              journeyMode={journeyMode}
              missingOnlyNome={missingOnlyNome}
              missingOnlyEmail={missingOnlyEmail}
            />
          )}
        </div>

        <div className="mt-12 text-center">
          <a
            href="/scopri"
            className="text-lg font-medium hover:underline"
            style={{ color: "#B40101" }}
            data-testid="funnel-howitworks-link"
          >
            Come funziona ValutaPro
          </a>
        </div>
      </div>
    </div>
  );
}

// ===================== STEPS =====================
function Step1({ data, upd, togglePertinenza, canNext, onNext, modalita, onChangeModalita, modalitaSource }) {
  const isAffitto = modalita === "affitto";
  const helperText = modalitaSource === "journey"
    ? "Preselezionato in base al tuo percorso OrizzontiPro. Puoi cambiarlo qui."
    : "Serve a impostare il tipo di analisi (compravendita o locazione). Puoi cambiarlo prima di iniziare.";
  return (
    <div className="vg-card p-6 sm:p-10">
      <h1 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A]" data-testid="step1-title">
        {isAffitto
          ? "Scopri il canone di locazione realistico del tuo immobile oggi."
          : "Scopri quanto è davvero vendibile il tuo immobile oggi."}
      </h1>
      <p className="text-base text-gray-500 mt-3">
        {isAffitto
          ? "Analisi gratuita in 90 secondi basata sui dati di mercato disponibili per la tua zona (locazione)."
          : "Analisi gratuita in 90 secondi basata sui dati di mercato disponibili per la tua zona."}
      </p>

      <div className="mt-6" data-testid="modalita-selector">
        <div className="text-sm font-semibold text-[#0A0A0A] mb-2">Cosa vuoi analizzare?</div>
        <div className="inline-flex rounded-[12px] border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => onChangeModalita("vendita")}
            className={`px-5 py-2 text-sm font-semibold transition-colors ${modalita === "vendita" ? "bg-[#B40101] text-white" : "bg-white text-[#0A0A0A] hover:bg-gray-50"}`}
            data-testid="modalita-vendita-btn"
          >
            Vendita
          </button>
          <button
            type="button"
            onClick={() => onChangeModalita("affitto")}
            className={`px-5 py-2 text-sm font-semibold transition-colors ${modalita === "affitto" ? "bg-[#B40101] text-white" : "bg-white text-[#0A0A0A] hover:bg-gray-50"}`}
            data-testid="modalita-affitto-btn"
          >
            Affitto
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">{helperText}</p>
      </div>

      <div className="space-y-6 mt-8">
        <Field label="Tipologia immobile">
          <select
            className="vg-input"
            value={data.tipologia}
            onChange={(e) => upd({ tipologia: e.target.value })}
            data-testid="tipologia-select"
          >
            <option value="">Seleziona…</option>
            {TIPOLOGIE.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </Field>

        <Field label="Dove si trova l'immobile?" help="Anche solo il quartiere è sufficiente. Più è preciso, più accurata sarà l'analisi.">
          <input
            type="text"
            className="vg-input"
            placeholder="Es. Via Mario Rossi 4, Roma · Prati, Roma · Milano centro"
            value={data.indirizzo}
            onChange={(e) => upd({ indirizzo: e.target.value })}
            data-testid="indirizzo-input"
          />
        </Field>

        <Field label="Metratura in mq">
          <input
            type="number" min="1"
            className="vg-input" placeholder="Es. 95"
            value={data.mq}
            onChange={(e) => upd({ mq: e.target.value })}
            data-testid="mq-input"
          />
        </Field>

        <Field label="Stato immobile">
          <select
            className="vg-input"
            value={data.stato_immobile}
            onChange={(e) => upd({ stato_immobile: e.target.value })}
            data-testid="stato-select"
          >
            <option value="">Seleziona…</option>
            {STATI.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </Field>

        <Field label="Piano dell'immobile">
          <select
            className="vg-input"
            value={data.piano}
            onChange={(e) => upd({ piano: e.target.value })}
            data-testid="piano-select"
          >
            <option value="">Seleziona…</option>
            {PIANI.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </Field>

        <Field label="Pertinenze (opzionale)">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {PERTINENZE.map((p) => {
              const active = data.pertinenze.includes(p);
              return (
                <button key={p}
                  onClick={() => togglePertinenza(p)}
                  className={`vg-pill-option ${active ? "selected" : ""}`}
                  data-testid={`pertinenza-${p.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className={`vg-checkbox ${active ? "checked" : ""}`}>
                    {active && <CheckCircle2 size={14} />}
                  </span>
                  {p}
                </button>
              );
            })}
          </div>
        </Field>
      </div>

      <div className="mt-10 flex justify-end">
        <button className="vg-btn-primary" disabled={!canNext} onClick={onNext} data-testid="step1-cta">
          Inizia l'analisi <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}

function YesNo({ value, onChange, testid }) {
  return (
    <div className="flex gap-3 mt-4">
      <button
        className={`vg-yn-btn ${value === true ? "selected-yes" : ""}`}
        onClick={() => onChange(true)}
        data-testid={`${testid}-yes`}
      >Sì</button>
      <button
        className={`vg-yn-btn ${value === false ? "selected-no" : ""}`}
        onClick={() => onChange(false)}
        data-testid={`${testid}-no`}
      >No</button>
    </div>
  );
}

function Step2({ data, upd, canNext, onNext, modalita }) {
  const isAffitto = modalita === "affitto";
  const questions = [
    { key: "doc", q: "La documentazione urbanistica e catastale è già verificata da un tecnico?" },
    { key: "ape", q: "Conosci la classe energetica aggiornata alle direttive europee vigenti?" },
    {
      key: "comp",
      q: isAffitto
        ? "Sai quanti immobili simili al tuo sono oggi proposti in affitto nella tua micro-zona?"
        : "Sai quanti immobili simili al tuo sono oggi in vendita nella tua micro-zona?",
    },
  ];
  const header = isAffitto
    ? "Tre aspetti che incidono sulla locazione del tuo immobile"
    : "Tre domande che i compratori si fanno sul tuo immobile";
  return (
    <div className="vg-card p-6 sm:p-10">
      <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]">{header}</h1>
      <div className="space-y-4 mt-8">
        {questions.map((q) => (
          <div key={q.key} className={`vg-toggle-card ${data[q.key] !== null ? "answered" : ""}`}>
            <p className="text-base font-medium text-[#0A0A0A]">{q.q}</p>
            <YesNo value={data[q.key]} onChange={(v) => upd({ [q.key]: v })} testid={`q-${q.key}`} />
          </div>
        ))}
      </div>
      <div className="mt-10 flex justify-end">
        <button className="vg-btn-primary" disabled={!canNext} onClick={onNext} data-testid="step2-cta">
          Vedi il risultato <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}

function Step3({ data, upd, canNext, onNext, modalita }) {
  const isAffitto = modalita === "affitto";
  const domanda = isAffitto
    ? "Hai già ricevuto una stima professionale aggiornata del canone di locazione negli ultimi 12 mesi?"
    : "Hai già ricevuto una valutazione professionale del tuo immobile negli ultimi 12 mesi?";
  const hint = isAffitto
    ? "Il canone richiesto e quello sostenibile dal mercato possono differire in base alla zona, allo stato e alle caratteristiche dell'immobile."
    : "La maggior parte dei proprietari scopre solo in questa fase che il valore percepito e quello di mercato differiscono anche del 15-20%.";
  return (
    <div className="vg-card p-6 sm:p-10">
      <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]">Un'ultima cosa prima del risultato</h1>
      <p className="text-base text-gray-500 mt-2">Aiutaci a calibrare meglio l'analisi.</p>
      <div className="vg-toggle-card mt-8 answered">
        <p className="text-base font-medium text-[#0A0A0A]">{domanda}</p>
        <YesNo value={data.valutazione} onChange={(v) => upd({ valutazione: v })} testid="q-val" />
        {data.valutazione === false && (
          <p className="italic text-sm text-gray-500 mt-3">
            {hint}
          </p>
        )}
      </div>
      <div className="mt-10 flex justify-end">
        <button className="vg-btn-primary" disabled={!canNext} onClick={onNext} data-testid="step3-cta">
          Continua <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}

function Step4({ data, upd, canNext, onNext, modalita }) {
  const isAffitto = modalita === "affitto";
  const tempistiche = getTempisticheView(modalita);
  return (
    <div className="vg-card p-6 sm:p-10">
      <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]">Quanto tempo hai?</h1>
      <p className="text-base text-gray-500 mt-2">
        {isAffitto
          ? "Entro quanto tempo vorresti mettere il tuo immobile in locazione?"
          : "Entro quanto tempo vorresti completare la vendita o l'affitto?"}
      </p>
      <div className="space-y-3 mt-8">
        {tempistiche.map((t) => {
          const sel = data.tempistica === t.value;
          return (
            <button key={t.value}
              onClick={() => upd({ tempistica: t.value })}
              className={`vg-pill-option ${sel ? "selected" : ""}`}
              data-testid={`tempistica-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span className={`vg-checkbox ${sel ? "checked" : ""}`} style={{ borderRadius: 999 }}>
                {sel && <span style={{ width: 10, height: 10, background: "#fff", borderRadius: 999 }} />}
              </span>
              <div className="flex flex-col text-left">
                <span className="font-semibold">{t.label}</span>
                <span className="text-xs text-gray-500">{t.help}</span>
              </div>
            </button>
          );
        })}
      </div>
      <div className="mt-10 flex justify-end">
        <button className="vg-btn-primary" disabled={!canNext} onClick={onNext} data-testid="step4-cta">
          Vedi il risultato <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}

function ScoreCircle({ score }) {
  const radius = 70;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (score / 100) * circ;
  let color = "#10B981";
  if (score < 70) color = "#F59E0B";
  if (score < 40) color = "#EF4444";
  return (
    <div className="relative inline-flex items-center justify-center" data-testid="score-circle">
      <svg width="180" height="180" viewBox="0 0 180 180">
        <circle cx="90" cy="90" r={radius} stroke="#ECECEC" strokeWidth="14" fill="none" />
        <circle
          cx="90" cy="90" r={radius} stroke={color} strokeWidth="14" fill="none"
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%", transition: "stroke-dashoffset 1.2s cubic-bezier(0.16,1,0.3,1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-5xl font-bold text-[#0A0A0A]" style={{ fontFamily: "Outfit" }}>{score}</span>
        <span className="text-xs text-gray-400 mt-1">su 100</span>
      </div>
    </div>
  );
}

function Step5({ score, noAnswers, onNext, modalita, hasIdentity, submit, submitting }) {
  const isAffitto = modalita === "affitto";
  const isFinal = !!hasIdentity;
  const ctaLabel = isFinal
    ? (submitting ? "Invio in corso…" : "Ricevi l'analisi completa")
    : "Sblocca il Piano di Azione gratuito";
  const onClickCta = () => {
    if (isFinal) {
      if (!submitting) submit();
    } else {
      onNext();
    }
  };
  return (
    <div className="vg-card p-6 sm:p-10 text-center">
      <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]">
        {isAffitto ? "Il tuo Indice di preparazione alla locazione" : "Il tuo Indice di Vendibilità"}
      </h1>
      <p className="text-sm text-gray-500 mt-1">Una stima preliminare calcolata sulle tue risposte.</p>
      <div className="my-8 flex justify-center">
        <ScoreCircle score={score} />
      </div>

      {noAnswers.length === 0 ? (
        <div className="rounded-[12px] p-6 mt-2" style={{ background: "#ECFDF5", border: "1px solid #10B981" }}>
          <p className="text-[#065F46] font-semibold">Tutto in ordine: hai risposto positivamente a tutti i controlli.</p>
          <p className="text-sm text-[#065F46] mt-2">Sblocca il piano di azione per ricevere l'analisi dettagliata della tua zona.</p>
        </div>
      ) : (
        <div className="text-left space-y-3">
          <p className="text-sm font-semibold text-[#0A0A0A] uppercase tracking-wide">Punti critici da affrontare</p>
          {noAnswers.map((n) => (
            <div className="vg-locked" key={n.key} data-testid={`critical-${n.key}`}>
              <Lock size={18} />
              <div>
                <p className="font-semibold text-[#0A0A0A]">{n.label}</p>
                <p className="text-xs text-gray-400">Descrizione completa disponibile nell'analisi via email</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {isFinal && (
        <p className="text-xs text-gray-500 mt-6" data-testid="step5-identity-note">
          Verrà inviata all'indirizzo email già associato al tuo percorso.
        </p>
      )}
      <button
        className="vg-btn-primary mt-4"
        onClick={onClickCta}
        disabled={submitting}
        data-testid="step5-cta"
      >
        {submitting ? <Loader2 className="animate-spin" size={18} /> : null}
        {ctaLabel}
        {!submitting && <ArrowRight size={18} />}
      </button>
    </div>
  );
}

function Step6({ data, upd, canSubmit, submit, submitting, journeyMode, missingOnlyNome, missingOnlyEmail }) {
  // Se manca solo uno tra nome/email, chiediamo esclusivamente quello.
  if (missingOnlyNome || missingOnlyEmail) {
    const askNome = missingOnlyNome;
    return (
      <div className="vg-card p-6 sm:p-10" data-testid={`step6-only-${askNome ? "nome" : "email"}`}>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]">
          Manca solo {askNome ? "il tuo nome" : "la tua email"}
        </h1>
        <p className="text-base text-gray-500 mt-2">
          {askNome
            ? "Ci serve un nome da usare nel report che riceverai via email."
            : "Ci serve un indirizzo email a cui inviare il report completo."}
        </p>
        <div className="mt-8">
          {askNome ? (
            <Field label="Nome">
              <input
                className="vg-input"
                value={data.nome}
                onChange={(e) => upd({ nome: e.target.value })}
                data-testid="nome-input"
                placeholder="Il tuo nome"
                autoFocus
              />
            </Field>
          ) : (
            <Field label="Email">
              <input
                type="email"
                className="vg-input"
                value={data.email}
                onChange={(e) => upd({ email: e.target.value })}
                data-testid="email-input"
                placeholder="latuaemail@dominio.it"
                autoFocus
              />
            </Field>
          )}
        </div>
        <div className="mt-8 flex items-center justify-between">
          <div className="flex items-start gap-2 text-xs text-gray-500">
            <Lock size={14} className="mt-0.5 flex-shrink-0" />
            <span>Dati protetti. Non condividiamo con terzi.</span>
          </div>
          <button className="vg-btn-primary" disabled={!canSubmit || submitting} onClick={submit} data-testid="step6-submit">
            {submitting ? (<><Loader2 className="animate-spin" size={18} /> Invio in corso…</>) : (<>Invia e ricevi l'analisi <ArrowRight size={18} /></>)}
          </button>
        </div>
      </div>
    );
  }
  if (journeyMode) {
    return (
      <div className="vg-card p-6 sm:p-10" data-testid="step6-journey-mode">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]">La tua analisi ValutaPro è pronta da inviare</h1>
        <p className="text-base text-gray-500 mt-2">
          Continuiamo dal tuo percorso: useremo l'identità già associata alla tua analisi OrizzontiPro.
        </p>
        <div className="mt-6 rounded-[12px] bg-[#FAF7F2] border border-[#EDE4D3] p-5 text-sm text-[#0A0A0A] leading-relaxed">
          <div><span className="text-[#6B6B6B]">Verrà inviata a</span> <strong className="text-[#0A0A0A]">{data.nome || "—"}</strong></div>
          <div className="mt-1"><span className="text-[#6B6B6B]">Email</span> <strong className="text-[#0A0A0A]">{data.email || "—"}</strong></div>
        </div>
        <div className="mt-4 p-4 rounded-[12px] bg-gray-50 border-l-4 border-[#B40101] text-sm text-[#0A0A0A] leading-relaxed" data-testid="step6-privacy-disclaimer">
          Riceverai l'analisi completa via email. Al termine potrai scegliere se chiedere il contatto con un Property Advisor.
        </div>
        <div className="mt-8 flex items-center justify-between">
          <div className="flex items-start gap-2 text-xs text-gray-500">
            <Lock size={14} className="mt-0.5 flex-shrink-0" />
            <span>Dati protetti. Non condividiamo con terzi.</span>
          </div>
          <button className="vg-btn-primary" disabled={!canSubmit || submitting} onClick={submit} data-testid="step6-submit">
            {submitting ? (<><Loader2 className="animate-spin" size={18} /> Invio in corso…</>) : (<>Invia e ricevi l'analisi <ArrowRight size={18} /></>)}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="vg-card p-6 sm:p-10">
      <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]">La tua analisi ValutaPro è pronta da inviare</h1>
      <p className="text-base text-gray-500 mt-2">Indicaci nome ed email: riceverai il report completo in pochi minuti.</p>

      <div className="space-y-5 mt-8">
        <Field label="Nome">
          <input className="vg-input" value={data.nome} onChange={(e) => upd({ nome: e.target.value })} data-testid="nome-input" placeholder="Il tuo nome" />
        </Field>
        <Field label="Email">
          <input className="vg-input" type="email" value={data.email} onChange={(e) => upd({ email: e.target.value })} data-testid="email-input" placeholder="tua@email.it" />
        </Field>
      </div>

      <div className="mt-6 flex items-start gap-2 text-xs text-gray-500">
        <Lock size={14} className="mt-0.5 flex-shrink-0" />
        <span>I tuoi dati sono protetti. Non condividiamo con terzi e non inviamo spam.</span>
      </div>

      <div className="mt-4 p-4 rounded-[12px] bg-gray-50 border-l-4 border-[#B40101] text-sm text-[#0A0A0A] leading-relaxed" data-testid="step6-privacy-disclaimer">
        Riceverai l'analisi completa via email. Al termine potrai scegliere se chiedere il contatto con un Property Advisor.
      </div>

      <div className="mt-8 flex justify-end">
        <button className="vg-btn-primary" disabled={!canSubmit || submitting} onClick={submit} data-testid="step6-submit">
          {submitting ? (<><Loader2 className="animate-spin" size={18} /> Invio in corso…</>) : (<>Invia e ricevi l'analisi <ArrowRight size={18} /></>)}
        </button>
      </div>
    </div>
  );
}

function Field({ label, help, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">{label}</label>
      {children}
      {help && <p className="text-xs text-gray-500 mt-2">{help}</p>}
    </div>
  );
}

function SuccessScreen({ nome, email, referral, score, noAnswers, whatsappUrl, modalita }) {
  const [resendBusy, setResendBusy] = React.useState(false);
  const [callOpen, setCallOpen] = React.useState(false);
  const isAffitto = (modalita || "vendita").toLowerCase() === "affitto";
  const fallback = `https://wa.me/393925822212?text=${encodeURIComponent("Ciao, ho appena ricevuto l'analisi ValutaGenio del mio immobile e vorrei un orientamento gratuito con un Property Advisor.")}`;
  const whatsappLink = whatsappUrl || fallback;

  React.useEffect(() => {
    if (email) markToolCompleted("valutapro", { email, nome, modalita: (modalita || "vendita").toLowerCase() });
  }, [email, nome, modalita]);

  const refQ = referral ? `&ref=${encodeURIComponent(referral)}` : "";
  const nextToolUrl = `/partenza?email=${encodeURIComponent(email || "")}${refQ}`;

  const topCritical = Array.isArray(noAnswers) && noAnswers.length > 0 ? noAnswers[0].label : null;
  const strengthLabel = !topCritical ? "Nessuna criticità dichiarata: parti da una base solida." : null;

  let scoreBand = "buono";
  if (score >= 80) scoreBand = "molto buono";
  else if (score >= 65) scoreBand = "buono";
  else if (score >= 50) scoreBand = "da migliorare";
  else scoreBand = "delicato";

  const handleResend = async () => {
    if (resendBusy || !email) return;
    setResendBusy(true);
    try {
      await api.post("/email/resend", { tool: "valutapro", email });
      toast.success("Email rispedita. Controlla la tua casella.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile rispedire ora. Riprova tra qualche minuto.");
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#1A1A1A" }}>
      <div className="max-w-2xl mx-auto px-4 pt-16 pb-12 text-white">
        <div className="flex justify-center mb-8">
          <div className="w-24 h-24 rounded-full flex items-center justify-center" style={{ background: "#B40101" }}>
            <CheckCircle2 size={48} color="#fff" />
          </div>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold mb-4 text-center" data-testid="success-title">Perfetto, {nome}!</h1>
        <p className="text-lg text-gray-300 max-w-xl mx-auto leading-relaxed text-center">
          {isAffitto
            ? <>Ti abbiamo inviato l'analisi ValutaPro (Affitto) via email</>
            : <>Ti abbiamo inviato l'analisi ValutaPro completa via email</>}
          {email ? <> (<strong className="text-white">{email}</strong>)</> : null}.
        </p>

        <div className="mt-8 space-y-3 max-w-xl mx-auto">
          <div className="rounded-xl p-4 border" style={{ background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.1)" }} data-testid="vp-insight-score">
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#FFB3B3] mb-1">
              {isAffitto ? "Indice di preparazione alla locazione" : "Indice di vendibilità"}
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>{score}</span>
              <span className="text-xs text-gray-400">su 100 · quadro {scoreBand}</span>
            </div>
          </div>
          <div className="rounded-xl p-4 border" style={{ background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.1)" }} data-testid="vp-insight-factor">
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#FFB3B3] mb-1">Fattore che incide di più</div>
            <p className="text-sm text-gray-200 leading-relaxed">
              {topCritical || strengthLabel}
            </p>
          </div>
        </div>

        <div className="mt-6 max-w-xl mx-auto p-4 rounded-lg text-sm leading-relaxed" style={{ background: "rgba(180,1,1,0.15)", color: "#FFD8D8" }} data-testid="vp-email-notice">
          {isAffitto
            ? "Nell'email trovi il quadro completo: analisi di zona per la locazione, stima del canone e punti da verificare."
            : "Nell'email trovi il quadro completo: analisi di zona, stima di valore e punti da verificare."}
          {" Se non arriva entro 2-3 minuti, controlla lo spam oppure "}
          <button
            className="underline font-semibold disabled:opacity-60"
            onClick={handleResend}
            disabled={resendBusy || !email}
            data-testid="vp-resend-btn"
          >
            {resendBusy ? "rispedizione in corso…" : "rispedisci l'email"}
          </button>.
        </div>

        {!isAffitto && (
          <div
            data-testid="vp-visibility-box"
            className="mt-6 max-w-xl mx-auto rounded-xl p-5 border"
            style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.12)" }}
          >
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#FFB3B3] mb-2">
              Prezzo di uscita e visibilità
            </div>
            <h3 className="text-lg font-bold text-white mb-3 leading-snug" style={{ fontFamily: "Outfit" }}>
              Il prezzo di uscita è anche una scelta di visibilità
            </h3>
            <div className="space-y-3 text-sm text-gray-200 leading-relaxed">
              <p>
                Il prezzo di uscita influenza anche chi vedrà il tuo immobile. Un prezzo collocato sopra le fasce impostate dai potenziali acquirenti nelle ricerche può far sì che l'annuncio non compaia tra i risultati del target che stai cercando. Meno visibilità può significare meno richieste e tempi di vendita più lunghi.
              </p>
              <p>
                Il valore indicato da ValutaPro è un riferimento di mercato, costruito sui dati disponibili. Le caratteristiche specifiche del tuo immobile e la strategia di vendita possono giustificare uno scostamento, da valutare insieme al professionista.
              </p>
              <p className="italic text-gray-100">
                La domanda diventa: quanto ci discostiamo dal valore di riferimento, perché e con quale strategia?
              </p>
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
          {isAffitto ? (
            <>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#B40101] hover:bg-[#8E0101] text-white font-semibold px-5 py-3 rounded-xl transition-colors"
                data-testid="vp-cta-talk-pa"
                onClick={() => setCallOpen(true)}
              >
                <Phone size={16} /> Scrivi al Property Advisor
              </a>
              <a
                href="/app"
                className="flex-1 inline-flex items-center justify-center gap-2 border border-white/40 text-white font-semibold px-5 py-3 rounded-xl hover:bg-white/10 transition-colors"
                data-testid="vp-cta-back-to-journey"
              >
                Torna al percorso <ArrowRight size={18} />
              </a>
            </>
          ) : (
            <>
              <a
                href={nextToolUrl}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#B40101] hover:bg-[#8E0101] text-white font-semibold px-5 py-3 rounded-xl transition-colors"
                data-testid="vp-cta-next-tool"
              >
                Continua con PartenzaPro <ArrowRight size={18} />
              </a>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 border border-white/40 text-white font-semibold px-5 py-3 rounded-xl hover:bg-white/10 transition-colors"
                data-testid="vp-cta-talk-pa"
                onClick={() => setCallOpen(true)}
              >
                <Phone size={16} /> Voglio parlare con il mio Property Advisor
              </a>
            </>
          )}
        </div>

        <p className="text-xs text-gray-400 mt-6 text-center">
          {isAffitto
            ? "Puoi contattare un Property Advisor solo se lo desideri. Nessuna chiamata automatica."
            : "Un Property Advisor ValutaGenio ti ricontatterà solo se lo richiedi. Nessuna chiamata automatica."}
        </p>
      </div>
    </div>
  );
}
